import { useRef, useState } from 'react';
import { uploadDocument as persistDocument } from '@/modules/documents/services';
import {
  getInitialUploadDocumentForm,
  validateUploadDocumentForm,
  validateDocumentUploadFile,
} from '@/modules/documents/utils';
import { toast } from '../../../../shared/hooks/use-toast';
import { entityLabel, replaceEntityRelations } from '../../../../shared/utils/entitySelection';

export function useUploadDocument({ clients, staff = [], onUpload, defaultManagedBy = '' }) {
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFileState] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState(() => getInitialUploadDocumentForm({ uploadedBy: defaultManagedBy }));
  const [isUploading, setIsUploading] = useState(false);
  const isUploadingRef = useRef(false);

  function updateField(field, value) {
    if (isUploadingRef.current) return;
    setError('');
    setForm((prev) => {
      if (field === 'clientId') {
        const selectedDocumentClient = clients.find((client) => String(client.id) === String(value));
        return {
          ...prev,
          clientId: value,
          client: selectedDocumentClient ? entityLabel(selectedDocumentClient) : '',
        };
      }

      if (field === 'staffIds') {
        const names = value.map(id => staff.find(member => String(member.id) === String(id))?.name).filter(Boolean);
        return { ...prev, uploadedBy: names.join('; ') || defaultManagedBy,
          relations: replaceEntityRelations(prev.relations, 'TEAM_MEMBER', value, staff) };
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  }

  function reset() {
    setUploadedFileState(null);
    setError('');
    setForm(getInitialUploadDocumentForm({ uploadedBy: defaultManagedBy }));
    setDragActive(false);
    setIsUploading(false);
    isUploadingRef.current = false;
  }

  function closeDialog() {
    if (isUploadingRef.current) return;
    setIsUploadDialogOpen(false);
    reset();
  }

  function openDialog() {
    setForm((current) => ({
      ...current,
      uploadedBy: current.uploadedBy || defaultManagedBy,
    }));
    setIsUploadDialogOpen(true);
  }

  function handleDrag(event) {
    event.preventDefault();
    event.stopPropagation();
    if (isUploadingRef.current) return;

    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDragActive(true);
    } else if (event.type === 'dragleave' && !event.currentTarget?.contains(event.relatedTarget)) {
      setDragActive(false);
    }
  }

  function handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    selectFiles(event.dataTransfer.files);
  }

  function handleFileInput(event) {
    selectFiles(event.target.files);
  }

  function selectFiles(files) {
    if (isUploadingRef.current || !files?.length) return;
    if (files.length > 1) { setError('Carica un solo file alla volta.'); return; }
    setUploadedFile(files[0]);
  }

  function setUploadedFile(file) {
    if (isUploadingRef.current) return;
    const fileError = file ? validateDocumentUploadFile(file) : '';
    setError(fileError);
    if (fileError) return;
    setUploadedFileState(file);
    if (file) setForm(current => ({ ...current, name: current.name?.trim() ? current.name : file.name.replace(/\.[^.]+$/, '') }));
  }

  async function submit() {
    if (isUploadingRef.current) {
      return;
    }

    const error = validateUploadDocumentForm({ form, uploadedFile });
    if (error) {
      setError(error);
      toast({ title: 'Documento non valido', description: error, variant: 'destructive' });
      return;
    }

    isUploadingRef.current = true;
    setIsUploading(true);
    setError('');

    try {
      const result = await persistDocument({
        file: uploadedFile,
        form,
      });
      if (!result.ok) {
        setError(result.message || 'Caricamento non riuscito. Riprova.');
        toast({ title: 'Caricamento non riuscito', description: result.message, variant: 'destructive' });
        return;
      }

      try {
        await onUpload(result.data.document);
      } catch {
        toast({ title: 'Documento salvato', description: 'Aggiorna la pagina per ricaricare l’elenco dei documenti.' });
      }
      setIsUploadDialogOpen(false);
      reset();
      toast({ title: 'Documento caricato', description: result.message });
    } catch (error) {
      setError(error.message || 'Caricamento non riuscito. Riprova.');
      return;
    } finally {
      isUploadingRef.current = false;
      setIsUploading(false);
    }

  }

  return {
    isUploadDialogOpen,
    isUploading,
    dragActive,
    uploadedFile,
    error,
    form,
    setUploadedFile,
    updateField,
    openDialog,
    closeDialog,
    handleDrag,
    handleDrop,
    handleFileInput,
    submit,
  };
}
